import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ListChecks, Plus, Trash2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  leadId: string;
  /** Submissions already captured on this lead, used to show progress. */
  submissions: Array<{ id: string; full_name: string; email: string | null }>;
}

interface Expected {
  id: string;
  full_name: string;
  email: string | null;
  notes: string | null;
  matched_submission_id: string | null;
}

function norm(s?: string | null) {
  return (s || "").trim().toLowerCase();
}

/** Parses pasted lines like "Jane Doe, jane@x.com" or "Jane Doe <jane@x.com>". */
function parseBulk(text: string): Array<{ full_name: string; email: string | null }> {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const angle = line.match(/^(.*?)<([^>]+)>$/);
      if (angle) return { full_name: angle[1].trim(), email: angle[2].trim() || null };
      const parts = line.split(/[,;\t]/).map((p) => p.trim()).filter(Boolean);
      const emailPart = parts.find((p) => p.includes("@")) || null;
      const namePart = parts.find((p) => !p.includes("@")) || emailPart || line;
      return { full_name: namePart, email: emailPart };
    })
    .filter((r) => r.full_name.length > 1);
}

export function ExpectedParticipantsCard({ leadId, submissions }: Props) {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [bulk, setBulk] = useState("");
  const [singleName, setSingleName] = useState("");
  const [singleEmail, setSingleEmail] = useState("");

  const rosterQ = useQuery({
    queryKey: ["intake-expected", leadId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("lead_expected_participants")
        .select("id, full_name, email, notes, matched_submission_id")
        .eq("lead_id", leadId)
        .order("full_name");
      if (error) throw error;
      return (data || []) as Expected[];
    },
  });

  const addMut = useMutation({
    mutationFn: async (rows: Array<{ full_name: string; email: string | null }>) => {
      if (!rows.length) throw new Error("Nothing to add");
      const { data: auth } = await supabase.auth.getUser();
      const { error } = await (supabase as any).from("lead_expected_participants").insert(
        rows.map((r) => ({
          lead_id: leadId,
          full_name: r.full_name,
          email: r.email ? r.email.toLowerCase() : null,
          created_by: auth?.user?.id ?? null,
        })),
      );
      if (error) throw error;
      return rows.length;
    },
    onSuccess: (n) => {
      qc.invalidateQueries({ queryKey: ["intake-expected", leadId] });
      toast.success(`${n} name${n === 1 ? "" : "s"} added to the expected list`);
      setAddOpen(false);
      setBulk("");
      setSingleName("");
      setSingleEmail("");
    },
    onError: (e: any) => toast.error(e?.message || "Could not add names"),
  });

  const delMut = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase as any).from("lead_expected_participants").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["intake-expected", leadId] }),
    onError: (e: any) => toast.error(e?.message || "Could not remove name"),
  });

  const roster = rosterQ.data || [];

  function isReceived(e: Expected) {
    if (e.matched_submission_id) return true;
    return submissions.some(
      (s) => (e.email && norm(s.email) === norm(e.email)) || norm(s.full_name) === norm(e.full_name),
    );
  }

  const received = roster.filter(isReceived);
  const missing = roster.filter((e) => !isReceived(e));

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base flex items-center gap-2">
          <ListChecks className="h-4 w-4" /> Expected participants
          {roster.length > 0 && (
            <Badge variant="secondary" className="font-normal">
              {received.length} of {roster.length} received
            </Badge>
          )}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="h-4 w-4 mr-1" /> Add names
        </Button>
      </CardHeader>
      <CardContent>
        {rosterQ.isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : roster.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Add the people expected on this programme. Anyone who fills in the form without being on this list is
            flagged straight away — that is how a link sent from the wrong programme gets caught.
          </p>
        ) : (
          <div className="space-y-3">
            {missing.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Still waiting ({missing.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {missing.map((e) => (
                    <Badge key={e.id} variant="outline" className="font-normal group">
                      {e.full_name}
                      <button
                        type="button"
                        className="ml-1 opacity-50 hover:opacity-100"
                        title="Remove from expected list"
                        onClick={() => delMut.mutate(e.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {received.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Received ({received.length})
                </p>
                <div className="flex flex-wrap gap-1">
                  {received.map((e) => (
                    <Badge key={e.id} variant="secondary" className="font-normal">
                      <CheckCircle2 className="h-3 w-3 mr-1" /> {e.full_name}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add expected participants</DialogTitle>
            <DialogDescription>
              Paste one person per line — "Jane Doe, jane@example.com" — or add a single person below.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Paste a list</Label>
              <Textarea
                rows={6}
                value={bulk}
                onChange={(e) => setBulk(e.target.value)}
                placeholder={"Jane Doe, jane@example.com\nJohn Smith, john@example.com"}
              />
              {bulk.trim() && (
                <p className="text-xs text-muted-foreground">{parseBulk(bulk).length} name(s) detected</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2 border-t pt-3">
              <div>
                <Label>Name</Label>
                <Input value={singleName} onChange={(e) => setSingleName(e.target.value)} placeholder="Jane Doe" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={singleEmail} onChange={(e) => setSingleEmail(e.target.value)} placeholder="jane@example.com" />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button
              disabled={addMut.isPending || (!bulk.trim() && singleName.trim().length < 2)}
              onClick={() => {
                const rows = bulk.trim()
                  ? parseBulk(bulk)
                  : [{ full_name: singleName.trim(), email: singleEmail.trim() || null }];
                addMut.mutate(rows);
              }}
            >
              {addMut.isPending && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Add
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
